import { useState } from 'react';
import { Pause, Play, RotateCcw } from 'lucide-react';
import RadioMusicVisual from './RadioMusicVisual';

const ROLE_LABELS = {
  listener: 'Listener', artist: 'Artist', producer: 'Producer',
  dj: 'DJ', music_fan: 'Music fan', other: 'Other',
};
const FEEDBACK_FIELDS = [
  ['standoutReason', 'Standout', 220],
  ['whatConnected', 'Connected', 320],
  ['whatDidNotConnect', 'Did not connect', 280],
  ['roomExperience', 'Room experience', 320],
];
const text = (value) => typeof value === 'string' ? value.trim() : '';
const positiveCount = (value) => Number.isSafeInteger(value) && value > 0 ? value : null;
const recordLabel = (record) => text(record?.title) && text(record?.artistName)
  ? `${record.title} - ${record.artistName}` : '';

const isDisplayableCard = (card) => card && card.status === 'approved' &&
  /^[a-zA-Z0-9_-]{1,100}$/.test(text(card.cardKey)) &&
  positiveCount(card.recordsHeardCount) && positiveCount(card.recordsRequired) &&
  card.recordsHeardCount >= card.recordsRequired && Object.prototype.hasOwnProperty.call(ROLE_LABELS, card.listenerRole) &&
  text(card.musicTaste) && FEEDBACK_FIELDS.every(([field]) => text(card[field]));

const excerpt = (value, limit) => {
  const points = Array.from(value);
  if (points.length <= limit) return value;
  const start = points.slice(0, limit).join('');
  const space = start.lastIndexOf(' ');
  return `${space > start.length - 60 ? start.slice(0, space) : start}\u2026`;
};

function ListenerPassCard({ card }) {
  const [expanded, setExpanded] = useState(false);
  const name = text(card.listenerDisplayName) || 'JG Radio listener';
  const handle = text(card.listenerPublicId);
  const canExpand = FEEDBACK_FIELDS.some(([field, , limit]) => Array.from(card[field]).length > limit);
  const records = Array.isArray(card.heardRecords) ? card.heardRecords.filter(recordLabel).slice(0, 4) : [];
  const moreRecords = positiveCount(card.additionalPublicRecordsCount);
  const feedbackId = `listener-feedback-${card.cardKey}`;

  return (
    <article className="hitlab-listener-proof-card" aria-label={`Listener Pass from ${name}`}>
      <div className="hitlab-listener-proof-identity">
        <div>
          <h4>{name}</h4>
          {handle && handle !== name ? <p className="hitlab-listener-proof-handle">@{handle}</p> : null}
        </div>
        <span className="hitlab-listener-proof-badge">Approved Listener Pass</span>
      </div>
      <div className="hitlab-listener-proof-record-count">
        <span>Records heard</span>
        <strong aria-label={`${card.recordsHeardCount} qualified records heard; ${card.recordsRequired} required for the pass`}>
          {card.recordsHeardCount} / {card.recordsRequired}
        </strong>
        <small>Qualified records / pass requirement</small>
      </div>
      {records.length ? (
        <div className="hitlab-listener-proof-records">
          <p>Public records heard</p>
          <ul aria-label="Public records heard">
            {records.map((record, index) => <li key={`${recordLabel(record)}-${index}`}>{recordLabel(record)}</li>)}
          </ul>
          {moreRecords ? <small>+{moreRecords} more public {moreRecords === 1 ? 'record' : 'records'} heard</small> : null}
        </div>
      ) : null}
      <dl className="hitlab-listener-proof-context">
        <div><dt>Role</dt><dd>{ROLE_LABELS[card.listenerRole]}</dd></div>
        <div><dt>Music taste</dt><dd>{card.musicTaste}</dd></div>
      </dl>
      <dl className="hitlab-listener-proof-responses" id={feedbackId}>
        {FEEDBACK_FIELDS.map(([field, label, limit]) => (
          <div key={field}>
            <dt>{label}</dt>
            <dd>
              {field === 'standoutReason' && recordLabel(card.standoutRecord)
                ? <p className="hitlab-listener-proof-standout-record">{recordLabel(card.standoutRecord)}</p> : null}
              <p>{expanded ? card[field] : excerpt(card[field], limit)}</p>
            </dd>
          </div>
        ))}
      </dl>
      {canExpand ? (
        <button type="button" className="hitlab-listener-proof-expand"
          aria-expanded={expanded} aria-controls={feedbackId}
          onClick={() => setExpanded((current) => !current)}>
          {expanded ? 'Show less feedback' : 'Read full feedback'}
        </button>
      ) : null}
    </article>
  );
}

const isDisplayableClip = (clip) => clip && /^[a-zA-Z0-9_-]{1,100}$/.test(text(clip.clipKey)) &&
  text(clip.listenerDisplayName) && Number.isFinite(clip.durationSeconds) &&
  clip.durationSeconds >= 10 && clip.durationSeconds <= 20 &&
  /^https:\/\/res\.cloudinary\.com\/joegalaxy\/(video|raw)\/upload\//.test(text(clip.audioUrl));
const timeLabel = (seconds) => `0:${String(Math.max(0, Math.floor(seconds))).padStart(2, '0')}`;

function VoiceClipCard({ clip, voicePreview }) {
  const active = voicePreview?.state?.clipKey === clip.clipKey;
  const status = active ? voicePreview.state.status : 'idle';
  const busy = status === 'loading';
  const playing = status === 'playing';
  const elapsed = active ? Math.min(clip.durationSeconds, voicePreview.state.elapsed || 0) : 0;
  const hasRecords = positiveCount(clip.recordsHeardCount) && positiveCount(clip.recordsRequired) && clip.recordsHeardCount >= clip.recordsRequired;
  const role = Object.prototype.hasOwnProperty.call(ROLE_LABELS, clip.listenerRole) ? ROLE_LABELS[clip.listenerRole] : '';
  const statusText = { idle: 'Ready to play', loading: 'Loading…', playing: 'Playing', paused: 'Paused', ended: 'Finished', error: 'Audio unavailable. Try again or open Full Radio.' }[status];
  const action = playing || busy ? 'Pause' : status === 'ended' ? 'Replay' : status === 'error' ? 'Retry' : 'Play';
  const ActionIcon = action === 'Pause' ? Pause : action === 'Play' ? Play : RotateCcw;
  return (
    <article className={`hitlab-voice-proof-card${playing ? ' is-playing' : ''}`} aria-label={`Voice drop from ${clip.listenerDisplayName}`}>
      <div className="hitlab-listener-proof-identity">
        <div><h4>{clip.listenerDisplayName}</h4>
          {text(clip.listenerPublicId) && clip.listenerPublicId !== clip.listenerDisplayName ? <p className="hitlab-listener-proof-handle">@{clip.listenerPublicId}</p> : null}
        </div>
        <span className="hitlab-listener-proof-badge">Approved voice drop</span>
      </div>
      {role || text(clip.musicTaste) || hasRecords ? <dl className="hitlab-listener-proof-context">
        {role ? <div><dt>Role</dt><dd>{role}</dd></div> : null}
        {hasRecords ? <div><dt>Records heard</dt><dd>{clip.recordsHeardCount} / {clip.recordsRequired} required</dd></div> : null}
        {text(clip.musicTaste) ? <div className="hitlab-voice-proof-taste"><dt>Music taste</dt><dd>{clip.musicTaste}</dd></div> : null}
      </dl> : null}
      <div className="hitlab-voice-proof-transport">
        <div className="hitlab-voice-proof-controls">
          <button type="button" className="hitlab-secondary-btn hitlab-voice-proof-play" onClick={() => voicePreview?.toggle(clip)}
            disabled={!voicePreview?.toggle} aria-label={`${action} voice drop from ${clip.listenerDisplayName}`}>
            <ActionIcon size={18} aria-hidden="true" focusable="false" />
            {action}
          </button>
          <div className="hitlab-voice-proof-progress">
            <div>
              <span>{timeLabel(elapsed)}</span>
              <RadioMusicVisual as="span" className="hitlab-voice-proof-bars" barCount={5} isPlaying={playing} />
              <span>{timeLabel(clip.durationSeconds)}</span>
            </div>
            <progress max={clip.durationSeconds} value={elapsed} aria-label={`Voice drop progress for ${clip.listenerDisplayName}`} />
          </div>
        </div>
        <p className="hitlab-voice-proof-status" role="status">{statusText}</p>
      </div>
    </article>
  );
}

export default function HitLabListenerProofSection({ proof, onOpenRadio, voicePreview }) {
  const cards = Array.isArray(proof?.feedbackCards)
    ? proof.feedbackCards.filter(isDisplayableCard).slice(0, 3) : [];
  const voiceCount = positiveCount(proof?.voiceDropProof?.approvedVoiceDropsAvailable);
  const clipKeys = new Set();
  const clips = Array.isArray(proof?.voiceDropProof?.clips) ? proof.voiceDropProof.clips.filter((clip) => {
    if (!isDisplayableClip(clip) || clipKeys.has(clip.clipKey)) return false;
    clipKeys.add(clip.clipKey);
    return true;
  }).slice(0, 4) : [];
  if (!cards.length && !voiceCount && !clips.length) return null;

  return (
    <section className="hitlab-listener-proof" aria-label="JG Radio Listener Pass proof">
      {cards.length ? <div className="hitlab-listener-proof-heading">
        <p className="hitlab-kicker">FROM THE JG RADIO ROOM</p>
        <h3>Real Listener Proof</h3>
        <p>Approved Listener Passes, with records heard and feedback in each listener&apos;s own words.</p>
      </div> : null}
      {cards.length ? (
        <div className="hitlab-listener-proof-grid">
          {cards.map((card) => <ListenerPassCard key={card.cardKey} card={card} />)}
        </div>
      ) : null}
      {clips.length ? <section className="hitlab-voice-proof" aria-label="Listener voice drops">
        <div className="hitlab-listener-proof-heading">
          <h3>Hear the listeners</h3>
          <p>Short, approved listener voice drops from JG Radio.</p>
        </div>
        <div className="hitlab-voice-proof-grid">
          {clips.map((clip) => <VoiceClipCard key={clip.clipKey} clip={clip} voicePreview={voicePreview} />)}
        </div>
      </section> : null}
      <div className="hitlab-listener-proof-footer">
        <div>
          <p>Listeners receive JGK for approved feedback. Positive feedback is not required.</p>
          {clips.length || voiceCount ? <p>Approved listener voice drops can also receive JGK.</p> : null}
          {voiceCount ? <p className="hitlab-listener-proof-voice-count">{voiceCount} approved listener voice {voiceCount === 1 ? 'drop' : 'drops'} in Radio rotation.</p> : null}
        </div>
        <button type="button" className="hitlab-secondary-btn" onClick={onOpenRadio} disabled={!onOpenRadio}>
          Open Full Radio
        </button>
      </div>
    </section>
  );
}
